import {
  defineStore,
} from "pinia";
import type {
  ImpersonationStartResponse, SessionState, User,
} from "~/types/auth";
import {
  useCognito,
} from "~/composables/useCognito";
import {
  useApiFetch,
} from "~/composables/useApiFetch";

const IMPERSONATION_STORAGE_KEY = "ai-tutor.impersonation";

type StoredImpersonationState = {
  expiresAt: string;
  token: string;
  user: User;
};

const INITIAL_STATE: SessionState = {
  ready: false,
  mode: "none",
  user: null,
  idToken: null,
  accessToken: null,
  impersonationExpiresAt: null,
  registrationPaymentStatus: null,
  requiresAgreement: null,
};

let initializePromise: Promise<void> | null = null;
let agreementStatusPromise: Promise<void> | null = null;
let registrationPaymentStatusRequest: {
  userSub: string;
  promise: Promise<any>;
} | null = null;

const joinUrl = (base: string, path: string): string => {
  if (!base) {
    return path;
  }
  if (base.endsWith("/") && path.startsWith("/")) {
    return `${base.slice(0, -1)}${path}`;
  }
  if (!base.endsWith("/") && !path.startsWith("/")) {
    return `${base}/${path}`;
  }
  return `${base}${path}`;
};

const canUseSessionStorage = (): boolean => typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const isExpired = (expiresAt: string | null | undefined): boolean => {
  if (!expiresAt) {
    return true;
  }
  const timestamp = new Date(expiresAt).getTime();
  if (!Number.isFinite(timestamp)) {
    return true;
  }
  return timestamp <= Date.now() + 5_000;
};

const readStoredImpersonation = (): StoredImpersonationState | null => {
  if (!canUseSessionStorage()) {
    return null;
  }

  const rawValue = window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as StoredImpersonationState;
    if (!parsed?.token || !parsed?.expiresAt || !parsed?.user || isExpired(parsed.expiresAt)) {
      window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    return null;
  }
};

export const useSessionStore = defineStore("session", {
  state: (): SessionState => ({...INITIAL_STATE}),

  getters: {
    isAuthenticated: (state): boolean => state.ready && !!state.user,
    isImpersonating: (state): boolean => state.mode === "impersonation" && !!state.user?.impersonation,
    userDisplayName: (state): string | null => {
      const user = state.user;
      if (!user) {
        return null;
      }

      const name = user.name?.trim();
      if (name) {
        return name;
      }

      return user.username?.trim() || user.email?.trim() || user.sub || null;
    },
    userEmail: (state): string | null => state.user?.email ?? null,
    userRoles: (state): string[] => state.user?.roles ?? [],
    isSystemAdmin: (state): boolean => !!state.user?.roles?.includes("AIL_ADMIN"),
    impersonationActorDisplayName: (state): string | null => {
      const actor = state.user?.impersonation?.actor;
      if (!actor) {
        return null;
      }
      return actor.name?.trim() || actor.username?.trim() || actor.email?.trim() || actor.sub;
    },
    impersonationActorEmail: (state): string | null => state.user?.impersonation?.actor.email ?? null,
  },

  actions: {
    clearSessionState(): void {
      this.mode = "none";
      this.user = null;
      this.idToken = null;
      this.accessToken = null;
      this.impersonationExpiresAt = null;
      this.registrationPaymentStatus = null;
      this.requiresAgreement = null;
    },

    persistImpersonationSession(payload: StoredImpersonationState): void {
      if (!canUseSessionStorage()) {
        return;
      }
      window.sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(payload));
    },

    clearImpersonationSession(): void {
      if (!canUseSessionStorage()) {
        return;
      }
      window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    },

    applyCognitoSession(payload: {user: User; tokens: {idToken: string; accessToken: string}}): void {
      this.ready = true;
      this.mode = "cognito";
      this.user = payload.user;
      this.idToken = payload.tokens.idToken;
      this.accessToken = payload.tokens.accessToken;
      this.impersonationExpiresAt = null;
      // Whoever this session belongs to, their agreement state has not been read
      // yet. ensureAgreementStatus() fetches it on the first guarded navigation.
      this.requiresAgreement = null;
    },

    applyImpersonationSession(
      payload: StoredImpersonationState,
      tokens?: {idToken: string; accessToken: string},
    ): void {
      this.ready = true;
      this.mode = "impersonation";
      this.user = payload.user;
      this.idToken = tokens?.idToken ?? this.idToken;
      this.accessToken = payload.token;
      this.impersonationExpiresAt = payload.expiresAt;
      this.persistImpersonationSession(payload);
    },

    /**
     * Resolve who the current user is. Deliberately answers from Cognito alone —
     * no backend call — because every route guard and the login page block on
     * this before they can decide whether to redirect. Anything that needs the
     * API (agreements, payment status) is fetched on demand afterwards.
     */
    async initialize(): Promise<void> {
      if (initializePromise) {
        await initializePromise;
        return;
      }
      if (this.ready) {
        return;
      }

      initializePromise = (async () => {
        if (import.meta.dev && typeof window !== "undefined" && window.location.hostname === "localhost") {
          const config = useRuntimeConfig();
          const mockFlag = String(config.public.mockApi ?? "").toLowerCase().trim();
          const mockEnabled = mockFlag === "1" || mockFlag === "true";

          if (mockEnabled) {
            this.ready = true;
            this.mode = "dev";
            this.user = {
              sub: "dev-user-123",
              username: "dev-user",
              email: "dev@localhost",
              name: "Dev User",
              roles: ["AIL_ADMIN", "TENANT_MANAGER", "USER"],
              tenant: "dev-tenant",
              status: "active",
            };
            this.idToken = "dev-token";
            this.accessToken = "dev-token";
            this.impersonationExpiresAt = null;
            return;
          }
        }

        try {
          const cognito = useCognito();
          const session = await cognito.getSession();
          const storedImpersonation = readStoredImpersonation();

          if (storedImpersonation && session) {
            this.applyImpersonationSession(storedImpersonation, session.tokens);
            this.requiresAgreement = false;
            return;
          }

          this.clearImpersonationSession();

          if (session) {
            this.applyCognitoSession(session);
          } else {
            this.clearSessionState();
            this.ready = true;
          }
        } catch (error: any) {
          console.error("[Auth] Session bootstrap failed:", error);
          this.clearImpersonationSession();
          this.clearSessionState();
          this.ready = true;
        }
      })();

      try {
        await initializePromise;
      } finally {
        initializePromise = null;
      }
    },

    /**
     * Read the agreement state once per session. Callers that guard a route must
     * await this; the session bootstrap must not, or a cold backend Lambda would
     * hold up the redirect decision for seconds.
     */
    async ensureAgreementStatus(): Promise<void> {
      if (this.requiresAgreement !== null) {
        return;
      }
      if (agreementStatusPromise) {
        await agreementStatusPromise;
        return;
      }

      agreementStatusPromise = this.checkAgreementsAcceptance();
      try {
        await agreementStatusPromise;
      } finally {
        agreementStatusPromise = null;
      }
    },

    async checkAgreementsAcceptance(): Promise<void> {
      if (!this.isAuthenticated || this.isImpersonating || this.mode === "dev") {
        this.requiresAgreement = false;
        return;
      }
      try {
        const apiFetch = useApiFetch();
        const data = await apiFetch<{requiresAcceptance: boolean}>("/api/agreements/check-acceptance");
        this.requiresAgreement = data.requiresAcceptance;
      } catch (err) {
        console.warn("Failed to check agreements acceptance:", err);
        this.requiresAgreement = false;
      }
    },

    async validatePostLoginAccess(): Promise<void> {
      const config = useRuntimeConfig();
      const mockFlag = String(config.public.mockApi ?? "").toLowerCase().trim();
      const mockEnabled = mockFlag === "1" || mockFlag === "true";
      if (mockEnabled) {
        return;
      }

      const baseUrl = String(config.public.apiBaseUrl ?? "").trim();
      const basePath = String(config.public.apiBasePath ?? "/api").trim() || "/api";
      const url = joinUrl(baseUrl, joinUrl(basePath, "/auth/access-status"));

      try {
        const response = await $fetch<{blocked: boolean; accessStatus: string}>(url, {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        });
        if (response.blocked) {
          const cognito = useCognito();
          cognito.signOut();
          this.clearSessionState();
          this.ready = true;
          throw new Error("ACCESS_BLOCKED");
        }
      } catch (err: any) {
        if (err?.message === "ACCESS_BLOCKED") {
          throw err;
        }
      }
    },

    async signIn(email: string, password: string): Promise<void> {
      const cognito = useCognito();
      const result = await cognito.signIn(email, password);

      this.clearImpersonationSession();
      this.applyCognitoSession(result);
      await this.validatePostLoginAccess();
      await this.ensureAgreementStatus();
    },

    async submitMfaCode(code: string): Promise<void> {
      const cognito = useCognito();
      const result = await cognito.submitMfaCode(code);

      this.clearImpersonationSession();
      this.applyCognitoSession(result);
      await this.validatePostLoginAccess();
      await this.ensureAgreementStatus();
    },

    async completeNewPassword(email: string, oldPassword: string, newPassword: string): Promise<void> {
      const cognito = useCognito();
      const result = await cognito.completeNewPassword(email, oldPassword, newPassword);

      this.clearImpersonationSession();
      this.applyCognitoSession(result);
      await this.validatePostLoginAccess();
      await this.ensureAgreementStatus();
    },

    async startImpersonation(userId: string): Promise<void> {
      if (this.mode === "dev") {
        throw new Error("Impersonation is not available in mock mode.");
      }

      if (this.isImpersonating) {
        throw new Error("Stop the current impersonation session before starting a new one.");
      }

      const cognito = useCognito();
      const token = await cognito.getAccessToken();
      const idToken = await cognito.getIdToken();

      if (!token) {
        throw new Error("Your administrator session has expired. Please sign in again.");
      }

      const config = useRuntimeConfig();
      const baseUrl = String(config.public.apiBaseUrl ?? "").trim();
      const basePath = String(config.public.apiBasePath ?? "/api").trim() || "/api";
      const url = joinUrl(baseUrl, joinUrl(basePath, `/auth/impersonation/start/${userId}`));

      const response = await $fetch<ImpersonationStartResponse>(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      this.applyImpersonationSession(
        {
          token: response.token,
          expiresAt: response.expiresAt,
          user: response.user,
        },
        {
          idToken: idToken ?? this.idToken ?? "",
          accessToken: token,
        },
      );
    },

    async stopImpersonation(options: {notifyServer?: boolean} = {}): Promise<void> {
      if (!this.isImpersonating || !this.accessToken) {
        return;
      }

      const shouldNotifyServer = options.notifyServer !== false;
      const impersonationToken = this.accessToken;

      if (shouldNotifyServer) {
        try {
          const config = useRuntimeConfig();
          const baseUrl = String(config.public.apiBaseUrl ?? "").trim();
          const basePath = String(config.public.apiBasePath ?? "/api").trim() || "/api";
          const url = joinUrl(baseUrl, joinUrl(basePath, "/auth/impersonation/stop"));

          await $fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${impersonationToken}`,
            },
          });
        } catch (error) {
          console.warn("[Auth] Failed to acknowledge impersonation stop:", error);
        }
      }

      this.clearImpersonationSession();

      const cognito = useCognito();
      const session = await cognito.getSession();
      if (session) {
        this.applyCognitoSession(session);
        return;
      }

      this.clearSessionState();
      this.ready = true;
    },

    async refreshTokens(): Promise<void> {
      if (this.mode === "impersonation") {
        return;
      }

      const cognito = useCognito();
      const tokens = await cognito.refreshSession();
      if (tokens) {
        this.idToken = tokens.idToken;
        this.accessToken = tokens.accessToken;
      }
    },

    async getAccessToken(): Promise<string | null> {
      if (this.mode === "dev") {
        return "dev-token";
      }

      if (this.mode === "impersonation") {
        if (!isExpired(this.impersonationExpiresAt) && this.accessToken) {
          return this.accessToken;
        }

        await this.stopImpersonation({notifyServer: false});
        return this.accessToken;
      }

      const cognito = useCognito();
      const token = await cognito.getAccessToken();
      if (token) {
        this.accessToken = token;
      } else {
        this.accessToken = null;
      }
      return token;
    },

    async getIdToken(): Promise<string | null> {
      if (this.mode === "dev") {
        return "dev-token";
      }
      if (this.mode === "impersonation") {
        return this.idToken;
      }

      const cognito = useCognito();
      const token = await cognito.getIdToken();
      if (token) {
        this.idToken = token;
      } else {
        this.idToken = null;
      }
      return token;
    },

    logout(): void {
      if (this.mode === "impersonation" && this.accessToken) {
        void this.stopImpersonation();
      } else {
        this.clearImpersonationSession();
      }

      const cognito = useCognito();
      cognito.signOut();

      this.$reset();
      this.clearSessionState();
      this.ready = true;

      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
    },

    async fetchRegistrationPaymentStatus(force = false): Promise<any> {
      if (!this.isAuthenticated) {
        this.registrationPaymentStatus = null;
        return null;
      }
      if (!force && this.registrationPaymentStatus && this.user?.status === "active") {
        return this.registrationPaymentStatus;
      }

      const userSub = this.user?.sub;
      if (!userSub) {
        return null;
      }
      if (registrationPaymentStatusRequest?.userSub === userSub) {
        return registrationPaymentStatusRequest.promise;
      }

      const requestPromise = (async () => {
        const apiFetch = useApiFetch();
        try {
          const data = await apiFetch<any>("/api/users/profile/registration-payment-status");
          if (this.user?.sub === userSub) {
            this.registrationPaymentStatus = data;
            if (data.status) {
              this.user.status = data.status;
            }
          }
          return data;
        } catch (err) {
          console.warn("Failed to fetch registration payment status:", err);
          return null;
        }
      })();

      registrationPaymentStatusRequest = {
        userSub,
        promise: requestPromise,
      };

      try {
        return await requestPromise;
      } finally {
        if (registrationPaymentStatusRequest?.promise === requestPromise) {
          registrationPaymentStatusRequest = null;
        }
      }
    },

    beginAuthenticationFlow(): void {
      if (typeof window !== "undefined") {
        navigateTo("/login");
      }
    },
  },
});
